import type { EndpointConfig, EndpointsRegistry, FetchRequestInit, RequestOptions } from './types'
import type { GenericObject } from '~/types/common'

const DEFAULT_XHR_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json'
}

/**
 * Creates an endpoints registry for a specific base URL
 */
export const createEndpointsRegistry = (
  baseUrl: string,
  endpointsConfig: Record<string, EndpointConfig>
): EndpointsRegistry => {
  const getEndpointConfig = (id: string): EndpointConfig => {
    const config = endpointsConfig[id]
    if (!config) {
      throw new Error(`Endpoint configuration not found for ID: ${id}`)
    }
    return config
  }

  const buildEndpointUrl = (id: string, options: RequestOptions = {}): string => {
    const { path } = getEndpointConfig(id)
    const { params = {}, queryParams = {} } = options

    let url: string = path
    Object.entries(params).forEach(([key, value]) => {
      url = url.replace(`:${key}`, encodeURIComponent(String(value)))
    })

    if (Object.keys(queryParams).length > 0) {
      const queryString = Object.entries(queryParams)
        .map(([key, value]) => {
          if (Array.isArray(value)) {
            // Join multiple values with commas
            const encodedValues = value.map((v) => encodeURIComponent(String(v))).join(',')
            return `${key}=${encodedValues}`
          }
          return `${key}=${encodeURIComponent(String(value))}`
        })
        .join('&')

      url += `?${queryString}`
    }

    return `${baseUrl}${url}`
  }

  const createRequest = (
    id: string,
    options: RequestOptions = {},
    body: GenericObject | null = null
  ): FetchRequestInit => {
    const config = getEndpointConfig(id)
    const url = buildEndpointUrl(id, options)

    const init: FetchRequestInit = {
      url,
      method: config.method,
      headers: {
        ...DEFAULT_XHR_HEADERS,
        ...options.headers
      },
      body: undefined as string | undefined
    }

    if (config.method !== 'GET' && body !== null) {
      init.body = JSON.stringify(body)
    }

    return init
  }

  return {
    buildEndpointUrl,
    getEndpointConfig,
    createRequest
  }
}
